import { useCallback } from 'react'
import {
  useNavigate,
  useLocation,
  useParams,
  useResolvedPath,
} from 'react-router'

export type To =
  | string
  | Partial<{ pathname: string; search: string; hash: string; state: unknown }>

export interface NavigateOptions {
  replace?: boolean
  state?: unknown
}

export interface AppNavigateFn {
  (to: To, options?: NavigateOptions): void
  (delta: number): void
}

export function useAppNavigate(): AppNavigateFn {
  const navigate = useNavigate()
  // Stable identity so callers can use it as a useEffect dependency.
  const fn = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') return navigate(to)
      const toArg = to as
        | string
        | Partial<{ pathname: string; search: string; hash: string }>
      // In v6 state always lives in the second options arg, never on the
      // location object. Extract state from the object descriptor (v5 compat)
      // and merge with an explicitly-passed options.state (options wins).
      const objectState =
        typeof to === 'object' && 'state' in to
          ? (to as { state?: unknown }).state
          : undefined
      const state = options?.state !== undefined ? options.state : objectState
      navigate(toArg, {
        replace: options?.replace,
        state,
      })
    },
    [navigate]
  )
  return fn as AppNavigateFn
}

export function useAppParams<
  T extends Record<string, string | undefined> = Record<
    string,
    string | undefined
  >,
>() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useParams() as any as T
}

export function useAppLocation() {
  return useLocation()
}

/**
 * v5→v6/v7 compat: v5 returned match.path/url (route pattern / matched url),
 * which EXCLUDES the splat remainder and is stable across sub-URLs.
 *
 * v6 with `v7_relativeSplatPath: true` made `useResolvedPath('.')` resolve to
 * that splat-excluded base. v7 dropped the flag and `useResolvedPath('.')` now
 * returns the FULL matched pathname INCLUDING the splat value — so inside a
 * `path="x/*"` layout route it grows every navigation (e.g. building a link as
 * `${url}/${id}` stacked ids: `/etiketter/A/B/C`). Strip the splat tail to
 * restore the stable base. NOTE: returns RESOLVED pathname (no `:param`
 * placeholders).
 */
export function useAppMatch() {
  const resolved = useResolvedPath('.')
  const splat = useParams()['*']
  let pathname = resolved.pathname
  if (splat) {
    const suffix = '/' + splat
    if (pathname.endsWith(suffix)) {
      pathname = pathname.slice(0, -suffix.length) || '/'
    }
  }
  return { path: pathname, url: pathname }
}
